import { cn } from "@/utils/utils";

/** The picture if there is one, initials if not. Size and shape come from the caller. */
export function Avatar({
  src,
  name,
  className,
  placeholder = "initials",
}: {
  src: string | null | undefined;
  name: string;
  className?: string;
  /**
   * What stands in for a missing picture.
   *
   * Initials in a list, where a row has to be told from the one under it. A
   * grey figure where the picture is the point and there is only one of them —
   * the member's own photograph, which legacy drew this way: the empty frame
   * reads as a photograph nobody has taken, where two large letters read as a
   * decision somebody made.
   */
  placeholder?: "initials" | "person";
}) {
  const initials = name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");

  if (!src && placeholder === "person")
    return (
      <span
        aria-hidden
        className={cn(
          "bg-surface-inset text-line-strong grid size-9 shrink-0 place-items-center overflow-hidden rounded-full",
          className,
        )}
      >
        {/* Head and shoulders, filled, running off the bottom of the frame —
            the shape legacy's `ProfileIcon` drew at #cccccc on #e7eaed. */}
        <svg viewBox="0 0 100 100" className="size-full" fill="currentColor">
          <circle cx="50" cy="38" r="19" />
          <path d="M50 63c-17.7 0-31 11-31 24.6V100h62V87.6C81 74 67.7 63 50 63Z" />
        </svg>
      </span>
    );

  return src ? (
    <img
      src={src}
      alt=""
      className={cn("bg-brand-700 size-9 shrink-0 rounded-full object-cover", className)}
    />
  ) : (
    <span
      aria-hidden
      className={cn(
        "bg-brand-700 text-subhead grid size-9 shrink-0 place-items-center rounded-full font-semibold text-white",
        className,
      )}
    >
      {initials || "?"}
    </span>
  );
}

/**
 * Turn a chosen file into a square 160px JPEG data URL, cropped to cover.
 * Done here so the server only ever stores a thumbnail, whatever was picked.
 */
export const readAvatar = (file: File) => readImage(file, 160);

/** A chosen file as a square JPEG data URL of the given size, cropped to cover. */
/**
 * A small JPEG of an image already held as a data URL — a list's thumbnail.
 *
 * `width` across, the height in proportion. Resolves to `null` when the image
 * cannot be read or drawn rather than failing whatever is being saved: the
 * thumbnail is an optimisation, and a listing without one is listed with its
 * full first photo instead.
 */
export async function coverOf(dataUrl: string, width = 320): Promise<string | null> {
  try {
    const image = new Image();
    image.src = dataUrl;
    await image.decode();
    const scale = Math.min(1, width / image.naturalWidth);
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
    const context = canvas.getContext("2d");
    if (!context) return null;
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL("image/jpeg", 0.72);
  } catch {
    return null;
  }
}

export function readImage(file: File, size: number): Promise<string> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = size;
      canvas.height = size;
      const scale = Math.max(size / image.width, size / image.height);
      const width = image.width * scale;
      const height = image.height * scale;
      canvas
        .getContext("2d")
        ?.drawImage(image, (size - width) / 2, (size - height) / 2, width, height);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL("image/jpeg", 0.85));
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("That file is not an image we can read."));
    };
    image.src = url;
  });
}

/**
 * The longest a picture may be once it is a data URL.
 *
 * `POST /members` stores the ID card and the photograph as data URLs in a
 * column that refuses more than 400,000 characters, and a refusal arrives
 * after twenty fields have been filled in. Well under it, so a picture is
 * never the thing that fails a submit.
 */
export const PICTURE_BUDGET = 380_000;

/** How the browser is asked to encode a picture, in order, until one fits. */
const RENDERINGS = [
  [1, 0.85],
  [1, 0.7],
  [0.75, 0.65],
  [0.5, 0.6],
] as const;

/**
 * A chosen file as a JPEG data URL that keeps its proportions.
 *
 * `readImage` crops to a square, which is right for a face and wrong for
 * everything else: an ID card is twice as wide as it is tall, and the halves a
 * square crop throws away are the halves the name and the birthday are printed
 * on. A card scanned through that is a card the reader cannot read.
 */
export function readPicture(file: File, maxSide = 1400): Promise<string> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      const render = (fraction: number, quality: number) => {
        const scale = Math.min(1, (maxSide * fraction) / Math.max(image.width, image.height));
        const canvas = document.createElement("canvas");
        canvas.width = Math.max(1, Math.round(image.width * scale));
        canvas.height = Math.max(1, Math.round(image.height * scale));
        canvas.getContext("2d")?.drawImage(image, 0, 0, canvas.width, canvas.height);
        return canvas.toDataURL("image/jpeg", quality);
      };
      // A photograph of a busy card can still be too long at full quality, so
      // it is re-encoded smaller until it fits rather than being refused later.
      let picture = "";
      for (const [fraction, quality] of RENDERINGS) {
        picture = render(fraction, quality);
        if (picture.length <= PICTURE_BUDGET) break;
      }
      resolve(picture);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("That file is not an image we can read."));
    };
    image.src = url;
  });
}

/** A data URL back as bytes, for the one API that takes a file rather than a string. */
export function dataUrlToBlob(dataUrl: string): Blob {
  const comma = dataUrl.indexOf(",");
  const type = /:(.*?);/.exec(dataUrl.slice(0, comma))?.[1] ?? "image/jpeg";
  const binary = atob(dataUrl.slice(comma + 1));
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type });
}
